import { GoogleGenAI, Type } from "@google/genai";
import { PokemonCard } from "../types";
import { pokemonService } from "./pokemonService";

// Enhanced AI Service with Vision capabilities
export const aiCardService = {
  /**
   * Advanced card identification using Gemini Vision
   * Can identify cards from photos even when API is down
   */
  async identifyCardFromImage(base64Image: string): Promise<{
    name: string;
    number?: string;
    set?: string;
    language?: string;
    confidence: number;
    alternativeNames?: string[];
  }> {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

    const imagePart = {
      inlineData: {
        mimeType: 'image/jpeg',
        data: base64Image,
      },
    };

    const textPart = {
      text: `
        Analyze this Pokemon TCG Card image with high precision.
        
        CRITICAL INSTRUCTIONS:
        1. Identify the EXACT Pokemon Name (English)
        2. Read the Card Number (e.g., "102/165", "25/102")
        3. Identify the Set Name (e.g., "Base Set", "Jungle", "Fossil")
        4. Detect the card language (English, Japanese, etc.)
        5. Look for special indicators: "Holo", "Reverse Holo", "1st Edition"
        6. If unsure, provide your top 3 guesses with confidence scores
        
        Return detailed JSON with all findings.
      `
    };

    const response = await ai.models.generateContent({
      model: 'gemini-2.0-flash-exp',  // Use latest vision model
      contents: { parts: [imagePart, textPart] },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            name: { type: Type.STRING, description: "Primary Pokemon name" },
            number: { type: Type.STRING, description: "Card number" },
            set: { type: Type.STRING, description: "Set name" },
            language: { type: Type.STRING, description: "Card language" },
            confidence: { type: Type.NUMBER, description: "Confidence 0-100" },
            isHolo: { type: Type.BOOLEAN, description: "Is holographic" },
            isFirstEdition: { type: Type.BOOLEAN, description: "Is 1st edition" },
            alternativeNames: {
              type: Type.ARRAY,
              items: { type: Type.STRING },
              description: "Alternative identifications if uncertain"
            },
            notes: { type: Type.STRING, description: "Additional observations" }
          },
          required: ["name", "confidence"]
        }
      }
    });

    return JSON.parse(response.text);
  },

  /**
   * Search cards using AI when API is unavailable
   * Uses Gemini with search grounding to find card info
   */
  async aiSearchCards(query: string): Promise<{
    cards: Partial<PokemonCard>[];
    source: 'ai' | 'api' | 'cache';
    confidence: number;
  }> {
    // First try the API
    try {
      const apiResult = await pokemonService.searchCards(query, 1);
      if (apiResult.data.length > 0) {
        return {
          cards: apiResult.data,
          source: 'api',
          confidence: 1.0
        };
      }
    } catch {
      console.log('[AI Search] API unavailable, using fallback');
    }

    // Fall back to AI-powered search
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

    const response = await ai.models.generateContent({
      model: "gemini-2.0-flash-exp",
      contents: `
        Search for Pokemon TCG cards matching: "${query}"
        
        Use Google Search to find current card data, prices, and images.
        Return the most relevant cards with accurate details.
        
        For each card include:
        - name
        - set name
        - card number
        - rarity
        - types
        - hp
        - estimated market price
        - image URL if available
      `,
      config: {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            cards: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  id: { type: Type.STRING },
                  name: { type: Type.STRING },
                  set: {
                    type: Type.OBJECT,
                    properties: {
                      name: { type: Type.STRING },
                      series: { type: Type.STRING },
                      releaseDate: { type: Type.STRING }
                    }
                  },
                  number: { type: Type.STRING },
                  rarity: { type: Type.STRING },
                  types: { type: Type.ARRAY, items: { type: Type.STRING } },
                  hp: { type: Type.STRING },
                  images: {
                    type: Type.OBJECT,
                    properties: {
                      small: { type: Type.STRING },
                      large: { type: Type.STRING }
                    }
                  },
                  tcgplayer: {
                    type: Type.OBJECT,
                    properties: {
                      prices: { type: Type.OBJECT }
                    }
                  }
                }
              }
            },
            totalResults: { type: Type.NUMBER },
            searchSource: { type: Type.STRING }
          }
        }
      }
    });

    const result = JSON.parse(response.text);
    return {
      cards: result.cards || [],
      source: 'ai',
      confidence: 0.85
    };
  },

  /**
   * Get card price estimate using AI when API prices unavailable
   */
  async estimateCardPrice(cardName: string, setName?: string, rarity?: string): Promise<{
    estimatedPrice: number;
    priceRange: { min: number; max: number };
    confidence: number;
    factors: string[];
  }> {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

    const response = await ai.models.generateContent({
      model: "gemini-2.0-flash-exp",
      contents: `
        Research current market price for: ${cardName}
        ${setName ? `from set: ${setName}` : ''}
        ${rarity ? `rarity: ${rarity}` : ''}
        
        Search TCGplayer, eBay sold listings, and other market sources.
        Provide realistic price estimate with confidence level.
      `,
      config: {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            estimatedPrice: { type: Type.NUMBER },
            priceRange: {
              type: Type.OBJECT,
              properties: {
                min: { type: Type.NUMBER },
                max: { type: Type.NUMBER }
              }
            },
            currency: { type: Type.STRING },
            confidence: { type: Type.NUMBER },
            factors: { type: Type.ARRAY, items: { type: Type.STRING } },
            lastUpdated: { type: Type.STRING },
            sources: { type: Type.ARRAY, items: { type: Type.STRING } }
          }
        }
      }
    });

    return JSON.parse(response.text);
  },

  /**
   * Advanced card grading with detailed analysis
   */
  async advancedCardGrading(base64Image: string): Promise<{
    grade: string;
    condition: number; // 1-10
    confidence: number;
    details: {
      centering: number;
      corners: number;
      edges: number;
      surface: number;
    };
    defects: string[];
    recommendations: string[];
    estimatedValue: {
      graded: number;
      ungraded: number;
    };
  }> {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

    const imagePart = {
      inlineData: {
        mimeType: 'image/jpeg',
        data: base64Image,
      },
    };

    const textPart = {
      text: `
        You are a professional PSA/BGS card grader with 20+ years experience.
        
        Analyze this Pokemon card image EXTREMELY carefully:
        
        1. CENTERING (0-10): Measure border symmetry
        2. CORNERS (0-10): Check for whitening, dings, wear
        3. EDGES (0-10): Check for whitening, chipping
        4. SURFACE (0-10): Check for scratches, print lines, holo damage
        
        Look for:
        - Micro-scratches on holo surface
        - Corner whitening (front and back)
        - Edge wear
        - Print dots/blemishes
        - Fingerprints
        - Any creases or bends
        
        Provide professional PSA/BGS grade estimate (1-10 scale and letter grade).
        Calculate estimated value difference between graded and ungraded.
        
        Be conservative but accurate. When in doubt, grade down.
      `
    };

    const response = await ai.models.generateContent({
      model: 'gemini-2.0-flash-exp',
      contents: { parts: [imagePart, textPart] },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            grade: { type: Type.STRING, description: "PSA/BGS grade (e.g., 'PSA 9', 'BGS 9.5')" },
            condition: { type: Type.NUMBER, description: "Numeric 1-10" },
            confidence: { type: Type.NUMBER },
            details: {
              type: Type.OBJECT,
              properties: {
                centering: { type: Type.NUMBER },
                corners: { type: Type.NUMBER },
                edges: { type: Type.NUMBER },
                surface: { type: Type.NUMBER }
              }
            },
            defects: { type: Type.ARRAY, items: { type: Type.STRING } },
            recommendations: { type: Type.ARRAY, items: { type: Type.STRING } },
            estimatedValue: {
              type: Type.OBJECT,
              properties: {
                graded: { type: Type.NUMBER },
                ungraded: { type: Type.NUMBER },
                currency: { type: Type.STRING }
              }
            }
          }
        }
      }
    });

    return JSON.parse(response.text);
  }
};
